import React from 'react';
import { getIntl } from '../../../../utils/intl';
import ExpandableInformationSection from './ExpandableInformationSection';
import ExpandableInformationSubsection from './ExpandableInformationSubsection';
import Text from '../../../v2BaseComponents/Text';
import Stack from '../../../v2BaseComponents/Stack';
import { getSpacing } from '../../../../utils/spacing';

const intl = getIntl('AboutThisAppSection', {
    aboutThisApp: 'About This App',
    aboutThisAppCollapsed: 'Providing better care starts with knowing the full story.',
    paragraph1:
        'Primary Record is the app designed for families to have one secure place to organize, collaborate, and share health data – bringing together invaluable information scattered across patient portals, three ring binders and post-it notes. With confidence, family members and trusted advocates are empowered to organize medical information for seamless sharing with those who matter most, enabling them to quickly retell the health journey before, between, and after appointments.',
    paragraph2:
        "We are also powering Primary Record Professional, a secure management system for care managers and patient advocates in one easy to use, secure portal. With frequent changes in health and complex moving parts, a centralized hub for an individual's care team is imperative. Seamless communication between caregivers, knowing when things change and ensuring a cohesive experience from doctor’s office to at-home care, results in better coordinated care and improved outcomes.",
    howItWorksTitle: 'How It Works',
    howItWorksDescription:
        'Primary Record allows family members to easily share health information while navigating through healthcare system transitions, visiting new physicians, and tracking health changes between appointments.',
    collectingTitle: 'Collecting',
    collectingDescription:
        'The software gives customers the flexibility to either input medical information manually or download certain electronic health data from available patient portals. When connecting to patient portals, it only retrieves a copy of the information into the Primary Record application, allowing customers to edit and make notes in one place.',
    organizingTitle: 'Organizing',
    organizingDescription:
        'Customers can link related health details, clarifying the what, when, who, and why for others. For instance, a medication record from a patient portal can be easily associated with its related conditions, the dispensing pharmacy, and the prescribing physician within Primary Record. This feature helps keep family members informed.',
    shareByInvitationTitle: 'Share By Invitation',
    shareByInvitationDescription:
        'The account owner, also referred to as the profile organizer, can grant collaborators access to view and edit the Medical Profile, ensuring it remains current. Unique to Primary Record, this feature allows organizers to control what invited collaborators can see and edit, based on the permissions set.',
    shareBySummaryTitle: 'Share by Health Summary',
    shareBySummaryDescription:
        'Reports can be generated and shared as a PDF or via a link, allowing customers to have current and summarized medical information to share. For example, keeping an accurate medication list in Primary Record allows organizers and invited collaborators to generate a report of current medications to share.',
    costTitle: 'Cost',
    costDescription:
        'Primary Record is free to use but also offers premium services when a customer needs additional services to use their health information for better care. The premium services are enabled with a subscription that can be turned on or off at any time.',
});

const sections = [
    {
        title: intl('howItWorksTitle'),
        contents: [intl('howItWorksDescription')],
    },
    {
        title: intl('collectingTitle'),
        contents: [intl('collectingDescription')],
    },
    {
        title: intl('organizingTitle'),
        contents: [intl('organizingDescription')],
    },
    {
        title: intl('shareByInvitationTitle'),
        contents: [intl('shareByInvitationDescription')],
    },
    {
        title: intl('shareBySummaryTitle'),
        contents: [intl('shareBySummaryDescription')],
    },
    {
        title: intl('costTitle'),
        contents: [intl('costDescription')],
    },
];

const AboutThisAppSection: React.FC = () => {
    return (
        <ExpandableInformationSection
            title={intl('aboutThisApp')}
            renderCollapsedContent={() => (
                <Text
                    variant="h5"
                    color="Gray-Text"
                    style={{
                        lineHeight: 26,
                    }}
                >
                    {intl('aboutThisAppCollapsed')}
                </Text>
            )}
            renderExpandedContent={() => (
                <Stack space={getSpacing(4)} vertical>
                    <Text
                        variant="h5"
                        color="Gray-Text"
                        style={{
                            lineHeight: 26,
                        }}
                    >
                        {intl('paragraph1')}
                    </Text>
                    <Text
                        variant="h5"
                        color="Gray-Text"
                        style={{
                            lineHeight: 26,
                        }}
                    >
                        {intl('paragraph2')}
                    </Text>
                    {sections.map((section, index) => (
                        <ExpandableInformationSubsection
                            key={index}
                            title={section.title}
                            contents={section.contents}
                        />
                    ))}
                </Stack>
            )}
        />
    );
};

export default React.memo(AboutThisAppSection);
